import useSWR from 'swr'
import { WorkflowTemplate } from '@digital-twins/shared-types'

import { workflowsApi } from '@/lib/api'

import { useWorkflowTemplates } from './useWorkflowTemplates'

/**
 * Detail data for a single workflow template, keyed by `slug` (Phase 2 of
 * docs/plans/assistant-platform-v1-phase2-integration-map.md, section 1.2:
 * "slug to token, resolve client-side").
 *
 * `GET /workflows/:token` takes the template's UUID `token`, never its slug
 * (`@IsUUID()` on the route param), so this hook composes `useWorkflowTemplates`
 * for its already-cached list, finds `summary.slug === slug`, and only then
 * fetches the full record by `summary.token`. The list is the widened
 * `includeThreadActions` catalogue (its own SWR key) so a Commands-page slug
 * can resolve; the lobby hook keeps the default hide.
 *
 * A slug absent from a list that has itself finished loading without error is
 * a genuine not-found (`template: null`, `isLoading: false`, `isError:
 * false`) — distinct from a list or detail fetch that failed outright
 * (`isError: true`), which must not render as "this workflow does not exist."
 */
export interface UseWorkflowTemplateResult {
  template: WorkflowTemplate | null
  isLoading: boolean
  isError: boolean
  retry: () => void
  /** Seat a server-returned detail record without refetching. */
  setTemplate: (next: WorkflowTemplate) => void
}

export function useWorkflowTemplate(
  slug: string | undefined,
  options: { enabled?: boolean } = {},
): UseWorkflowTemplateResult {
  const enabled = options.enabled !== false
  const {
    templates,
    isLoading: isListLoading,
    isError: isListError,
    retry: retryList,
  } = useWorkflowTemplates({ includeThreadActions: true, enabled })

  const summary =
    slug !== undefined && enabled
      ? templates.find((candidate) => candidate.slug === slug) ?? null
      : null

  const {
    data: template,
    isLoading: isDetailLoading,
    error: detailError,
    mutate: mutateDetail,
  } = useSWR(summary ? ['workflow-template', summary.token] : null, () =>
    workflowsApi.getTemplate(summary!.token),
  )

  function setTemplate(next: WorkflowTemplate) {
    if (!summary) {
      return
    }
    void mutateDetail(next, { revalidate: false })
  }

  return {
    template: template ?? null,
    // While the list is still loading, a slug cannot yet be judged "not found":
    // `summary` would be null purely because `templates` defaults to `[]`.
    isLoading: enabled && (isListLoading || (summary !== null && isDetailLoading)),
    isError: isListError || Boolean(detailError),
    retry: () => {
      retryList()
      void mutateDetail()
    },
    setTemplate,
  }
}
